
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { verifyStorageBucket } from '@/lib/api/paper-storage/validation';
import { 
  getPublicUrl, 
  listFilesInFolder 
} from '@/lib/api/paper-storage/storage-service';
import { supabase } from '@/lib/supabase';

interface DiagnosticResult {
  name: string;
  status: 'success' | 'warning' | 'error';
  message: string;
  details?: string;
}

export const StorageDiagnosticTool: React.FC = () => {
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const runDiagnostics = async () => {
    setIsRunning(true);
    setResults([]);
    const diagnosticResults: DiagnosticResult[] = [];

    try {
      // Check 1: Authentication
      const { data: { user } } = await supabase.auth.getUser();
      diagnosticResults.push({
        name: 'Authentication',
        status: user ? 'success' : 'error',
        message: user 
          ? 'User is authenticated' 
          : 'User is not authenticated. Please sign in to use storage features.',
        details: user ? `User ID: ${user.id}` : undefined
      });

      // Check 2: Bucket Existence
      const bucketExists = await verifyStorageBucket();
      diagnosticResults.push({
        name: 'Storage Bucket',
        status: bucketExists ? 'success' : 'error',
        message: bucketExists 
          ? 'Papers storage bucket exists and is accessible' 
          : 'Papers storage bucket does not exist or is not accessible',
      });

      // Check 3: List Buckets Permission
      try {
        const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
        
        diagnosticResults.push({
          name: 'List Buckets Permission',
          status: bucketsError ? 'error' : 'success',
          message: bucketsError 
            ? 'Cannot list storage buckets' 
            : `Successfully listed ${buckets?.length || 0} buckets`,
          details: bucketsError 
            ? bucketsError.message 
            : buckets?.map(b => b.name).join(', ')
        });
      } catch (e: any) {
        diagnosticResults.push({
          name: 'List Buckets Permission',
          status: 'error',
          message: 'Error checking bucket permissions',
          details: e.message
        });
      }

      // Check 4: URL Generation
      try {
        const testPath = 'test/test.pdf';
        const publicUrl = getPublicUrl(testPath);
        
        diagnosticResults.push({
          name: 'URL Generation',
          status: publicUrl ? 'success' : 'error',
          message: publicUrl 
            ? 'Public URL generation is working' 
            : 'Failed to generate public URL',
          details: publicUrl ? `Sample URL: ${publicUrl}` : undefined
        });
      } catch (e: any) {
        diagnosticResults.push({
          name: 'URL Generation',
          status: 'error',
          message: 'Error generating public URL',
          details: e.message
        });
      }

      // Check 5: List Files Permission
      if (user) {
        try {
          const files = await listFilesInFolder(user.id);
          
          diagnosticResults.push({
            name: 'List Files Permission',
            status: files !== null ? 'success' : 'error',
            message: files !== null 
              ? `Successfully listed files in user folder (${files.length} files)` 
              : 'Cannot list files in user folder',
          });
        } catch (e: any) {
          diagnosticResults.push({
            name: 'List Files Permission',
            status: 'error',
            message: 'Error listing files in user folder',
            details: e.message
          });
        }
      }

      // Check 6: RLS Policies
      try {
        // Check for existing policies
        const { data: storagePolicies, error: policiesError } = await supabase
          .from('storage.policies')
          .select('name, definition')
          .eq('bucket_id', 'papers');
          
        const hasPolicies = !policiesError && storagePolicies && storagePolicies.length > 0;
        
        // Try to access another user's folder - should fail if RLS is working correctly
        const { data: _, error: rlsError } = await supabase.storage
          .from('papers')
          .list('some-other-user-id');
          
        const rlsWorking = rlsError && rlsError.message.includes('permission');
        
        const status = hasPolicies && rlsWorking ? 'success' : 'warning';
        const message = hasPolicies 
          ? (rlsWorking 
              ? 'RLS policies are configured and working correctly' 
              : 'RLS policies exist but may not be properly enforcing access controls')
          : 'No RLS policies found for the papers bucket';
          
        diagnosticResults.push({
          name: 'Row Level Security',
          status: status,
          message: message,
          details: hasPolicies 
            ? `Found ${storagePolicies.length} policies for papers bucket. RLS enforcement: ${rlsWorking ? 'Working' : 'Needs review'}`
            : 'No policies found. Consider adding RLS policies to protect your storage data.'
        });
      } catch (e: any) {
        // This is actually expected and good if it's a permissions error - it means RLS is working
        const isPermissionError = e.message && e.message.toLowerCase().includes('permission');
        
        diagnosticResults.push({
          name: 'Row Level Security',
          status: isPermissionError ? 'success' : 'warning',
          message: isPermissionError 
            ? 'RLS policies are working correctly (permission denied as expected)' 
            : 'Error checking RLS policies',
          details: e.message
        });
      }
    } catch (error: any) {
      diagnosticResults.push({
        name: 'General Diagnostic',
        status: 'error',
        message: 'Unexpected error during diagnostics',
        details: error.message
      });
    } finally {
      setResults(diagnosticResults);
      setIsRunning(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Storage System Diagnostic</CardTitle>
        <CardDescription>
          Test the connectivity and configuration of the file storage system
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {results.length > 0 ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">Diagnostic Results</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </Button>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              {results.map((result, index) => (
                <div key={index} className="bg-card rounded-lg border p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {result.status === 'success' && <CheckCircle className="h-5 w-5 text-green-500" />}
                      {result.status === 'warning' && <AlertCircle className="h-5 w-5 text-amber-500" />}
                      {result.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                      <span className="font-medium">{result.name}</span>
                    </div>
                    <span 
                      className={`text-xs px-2 py-1 rounded-full ${
                        result.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 
                        result.status === 'warning' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' : 
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}
                    >
                      {result.status}
                    </span>
                  </div>
                  
                  <p className="mt-1 text-sm text-muted-foreground">{result.message}</p>
                  
                  {showDetails && result.details && (
                    <p className="mt-2 text-xs p-2 bg-muted rounded overflow-x-auto">
                      {result.details}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Alert>
            <AlertTitle>Storage Diagnostics</AlertTitle>
            <AlertDescription>
              Click the button below to test the storage system and identify any issues.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      
      <CardFooter>
        <Button 
          onClick={runDiagnostics} 
          disabled={isRunning}
          className="w-full"
        >
          {isRunning ? 'Running Diagnostics...' : 'Run Diagnostics'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default StorageDiagnosticTool;
